/**
 * Owners in the lists and on a record: the filter, the bulk Assign owner
 * bar, a person's owner, and sending people through a rotation.
 *
 * Who may do what is the server's call (assign_records to give anyone's
 * record to anyone; without it, take an unowned one or let go of your own);
 * these only offer what the person can do, from the same permissions.
 */
import { useEffect, useState } from 'react';
import {
  Alert, Anchor, Badge, Button, Group, Loader, Modal, Progress, Select, Stack, Table, Text,
} from '@mantine/core';
import { Link } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { IconArrowsShuffle, IconUserCheck, IconX } from '@tabler/icons-react';
import {
  useAssignmentGroups, useAssignmentJob, useAssignPeople, useBulkOwner, useSetPersonOwner, type OwnedKind,
} from '../../api/assignment';
import type { LeadFilters } from '../../api/queries';
import { useAuth } from '../../auth/AuthProvider';
import { formatNumber } from '../../theme';
import { useVocab, type Noun } from '../../vocab';
import { OwnerSelect, useCrmPermissions, useOwnerOptions } from '../crm/crmShared';

/** Anyone, me, nobody or one member: as `owner_user_id` takes it. */
export function OwnerFilterSelect({
  value, onChange, w = 170,
}: { value: string; onChange: (v: string) => void; w?: number }) {
  const v = useVocab();
  const owners = useOwnerOptions();
  return (
    <Select
      aria-label={v.One('owner')}
      placeholder={`Any ${v.one('owner')}`}
      value={value || null}
      onChange={(x) => onChange(x ?? '')}
      clearable
      searchable
      w={w}
      data={[
        { value: 'me', label: 'Mine' },
        { value: 'none', label: 'Nobody' },
        ...owners.filter((o) => !o.label.endsWith('(you)')),
      ]}
    />
  );
}

/**
 * What to do with the rows ticked on a list: give them all to one owner,
 * and for people, send them through a rotation.
 */
export function BulkAssignBar({
  kind, ids, onDone, onClear,
}: { kind: OwnedKind; ids: string[]; onDone?: () => void; onClear: () => void }) {
  const v = useVocab();
  const perms = useCrmPermissions();
  const bulk = useBulkOwner(kind);
  const [owner, setOwner] = useState<string | null>(null);
  const [rotating, setRotating] = useState(false);
  if (!perms.assignRecords || ids.length === 0) return null;
  const noun: Noun = kind === 'people' ? 'person' : kind === 'companies' ? 'company' : 'deal';

  async function apply(to: string | null) {
    try {
      const r = await bulk.mutateAsync({ ids, owner: to });
      notifications.show({
        color: r.skipped.length ? 'yellow' : 'teal',
        title: 'Owner assigned',
        message: `${v.count(r.updated, noun)} changed.${r.skipped.length ? ` ${formatNumber(r.skipped.length)} skipped: ${r.skipped[0]?.reason}` : ''}`,
      });
      setOwner(null);
      onDone?.();
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not assigned', message: (e as Error).message });
    }
  }

  return (
    <Group gap="xs" wrap="wrap" data-testid="bulk-assign" p="xs" style={{ borderRadius: 8, background: 'var(--rs-surface2)' }}>
      <Badge variant="light" color="gray" tt="none">{`${formatNumber(ids.length)} selected`}</Badge>
      <OwnerSelect
        aria-label="New owner"
        placeholder={`Assign ${v.one('owner')}`}
        value={owner}
        onChange={setOwner}
        clearable={false}
        w={200}
        size="xs"
      />
      <Button size="xs" leftSection={<IconUserCheck size={14} />} disabled={!owner} loading={bulk.isPending} onClick={() => apply(owner)}>
        Assign owner
      </Button>
      <Button size="xs" variant="default" onClick={() => apply(null)} disabled={bulk.isPending}>
        Clear owner
      </Button>
      {kind === 'people' && (
        <Button size="xs" variant="default" leftSection={<IconArrowsShuffle size={14} />} onClick={() => setRotating(true)}>
          Assign through rotation
        </Button>
      )}
      <Button size="xs" variant="subtle" color="gray" leftSection={<IconX size={14} />} onClick={onClear}>
        Clear selection
      </Button>
      {kind === 'people' && (
        <AssignPeopleModal opened={rotating} personIds={ids} onClose={() => { setRotating(false); onDone?.(); }} />
      )}
    </Group>
  );
}

/**
 * Send people through a rotation, by id or by the records page's filter,
 * and watch it happen: queued, worked in the background, then who was not
 * assigned and why.
 */
export function AssignPeopleModal({
  opened, onClose, personIds, filter,
}: {
  opened: boolean;
  onClose: () => void;
  personIds?: string[];
  filter?: LeadFilters;
}) {
  const v = useVocab();
  const groups = useAssignmentGroups(opened).data?.groups ?? [];
  const start = useAssignPeople();
  const [group, setGroup] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const job = useAssignmentJob(jobId);
  useEffect(() => {
    if (!opened) {
      setJobId(null);
      setGroup(null);
      start.reset();
    }
  }, [opened]); // eslint-disable-line react-hooks/exhaustive-deps

  const d = job.data;
  const done = d && (d.job.status === 'done' || d.job.status === 'failed');
  const who = personIds ? v.count(personIds.length, 'person') : `every ${v.one('person')} matching the filters`;

  return (
    <Modal opened={opened} onClose={onClose} title="Assign through a rotation" size="lg">
      <Stack gap="sm">
        {!jobId && (
          <>
            <Text size="sm">{`Send ${who} through a rotation. Each goes to the next rep who can take them, within the reps' caps, days and hours, and the rest are listed with why.`}</Text>
            <Select
              label="Rotation"
              placeholder="Choose a rotation"
              data={groups.filter((g) => g.active).map((g) => ({ value: g.id, label: `${g.name} (${g.campaign_name})` }))}
              value={group}
              onChange={setGroup}
              nothingFoundMessage="No rotations yet. Add one in Settings, Teams."
            />
            {start.error && <Alert color="red" variant="light">{start.error.message}</Alert>}
            <Group justify="flex-end">
              <Button variant="default" onClick={onClose}>Cancel</Button>
              <Button
                disabled={!group}
                loading={start.isPending}
                onClick={async () => {
                  const j = await start.mutateAsync({
                    group_id: group!,
                    person_ids: personIds ?? null,
                    filter: personIds ? null : { ...(filter ?? {}), page: undefined, page_size: undefined },
                  });
                  setJobId(j.id);
                }}
              >
                Assign
              </Button>
            </Group>
          </>
        )}
        {jobId && !d && <Group justify="center"><Loader /></Group>}
        {d && (
          <Stack gap="sm" data-testid="assign-job">
            <Group justify="space-between">
              <Text size="sm" fw={600}>{d.job.group_name}</Text>
              <Badge color={d.job.status === 'failed' ? 'red' : done ? 'chartGreen' : 'gray'} variant="light">
                {d.job.status === 'queued' ? 'Queued' : d.job.status === 'running' ? 'Working' : d.job.status === 'done' ? 'Done' : 'Failed'}
              </Badge>
            </Group>
            <Progress value={d.job.total ? (d.job.processed / d.job.total) * 100 : 0} />
            <Text size="sm" data-testid="assign-job-summary">
              {`${formatNumber(d.job.assigned)} assigned, ${formatNumber(d.job.unassigned)} not assigned, of ${formatNumber(d.job.total)}.`}
            </Text>
            {d.job.error && <Alert color="red" variant="light">{d.job.error}</Alert>}
            {d.not_assigned.length > 0 && (
              <Table verticalSpacing={4} data-testid="assign-job-problems">
                <Table.Thead>
                  <Table.Tr><Table.Th>{v.One('person')}</Table.Th><Table.Th>Why not</Table.Th></Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {d.not_assigned.slice(0, 50).map((i) => (
                    <Table.Tr key={i.person_id}>
                      <Table.Td>
                        <Anchor component={Link} to={`/people/${encodeURIComponent(i.person_id)}`} size="sm" ff="monospace" onClick={onClose}>
                          {i.person_id}
                        </Anchor>
                      </Table.Td>
                      <Table.Td><Text size="sm">{i.reason}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
            <Group justify="flex-end"><Button onClick={onClose}>{done ? 'Close' : 'Keep working in the background'}</Button></Group>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}

/**
 * A person's owner on their page: who it is, and what the caller may do
 * about it.
 */
export function PersonOwner({
  personId, ownerId, ownerName, canSetOwner, canAssign,
}: {
  personId: string;
  ownerId: string | null | undefined;
  ownerName: string | null | undefined;
  canSetOwner: boolean;
  canAssign: boolean;
}) {
  const v = useVocab();
  const { user } = useAuth();
  const perms = useCrmPermissions();
  const set = useSetPersonOwner();
  const [rotating, setRotating] = useState(false);
  if (!perms.owners) return null;
  const mine = !!ownerId && ownerId === user?.id;

  async function change(owner: string | null) {
    try {
      const r = await set.mutateAsync({ id: personId, owner });
      notifications.show({
        color: 'teal',
        title: r.owner_name ? `Assigned to ${r.owner_name}` : `No ${v.one('owner')}`,
        message: `The change is on the timeline.`,
      });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not changed', message: (e as Error).message });
    }
  }

  return (
    <Group gap="sm" align="center" data-testid="person-owner">
      <Text size="sm" c="dimmed">{`${v.One('owner')}:`}</Text>
      {canAssign ? (
        <OwnerSelect
          aria-label={v.One('owner')}
          value={ownerId ?? null}
          onChange={(o) => change(o)}
          size="xs"
          w={210}
        />
      ) : (
        <Text size="sm" fw={500}>{ownerName ?? 'Nobody'}</Text>
      )}
      {!canAssign && canSetOwner && !ownerId && (
        <Button size="xs" variant="default" loading={set.isPending} onClick={() => change('me')}>Take it</Button>
      )}
      {!canAssign && mine && (
        <Button size="xs" variant="subtle" color="gray" loading={set.isPending} onClick={() => change(null)}>Let go</Button>
      )}
      {canAssign && perms.assignRecords && (
        <Button size="xs" variant="default" leftSection={<IconArrowsShuffle size={14} />} onClick={() => setRotating(true)}>
          Assign through rotation
        </Button>
      )}
      <AssignPeopleModal opened={rotating} personIds={[personId]} onClose={() => setRotating(false)} />
    </Group>
  );
}
